import React from 'react';
import { Button, Modal, ModalHeader } from 'reactstrap';
import IntlMessages from 'util/IntlMessages';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import FormErrors from 'components/formErrors/FormErrors';
import CardBox from 'components/CardBox';
import { isValueEntered, isNameValid } from 'services/Validation';
import { isDateBefore } from 'services/DateService';
import { formatDateDefault } from 'services/DateFormatters';

class ProjectIssueAdd extends React.Component {
    constructor(props) {
        super(props);
        this.handleStartDateChange = this.handleStartDateChange.bind(this);
        this.handleEndDateChange = this.handleEndDateChange.bind(this);

        const { id, orgid, thumb, name, description, startDate, endDate, priority, starred, selected, frequently } = props.projectIssue;
        this.state = {
            id,
            orgid,
            thumb,
            name,
            description,
            startDate,
            endDate,
            priority,
            starred,
            selected,
            frequently,
            nameValid: isNameValid(name),
            startDateValid: isValueEntered(startDate),
            endDateValid: isValueEntered(endDate),
            formErrors: { name: '', startDate: '', endDate: '', dates: '' },
            datesValid: false,
            formValid: false,
        };
    }

    componentDidMount() {
        this.validateForm();
    }

    handleUserInput = (e) => {
        const name = e.target.name;
        const value = e.target.value;
        this.setState(
            { [name]: value },
            () => { this.validateField(name, value); } // run this call back on state change
        );
    }

    validateField(fieldName, value) {
        let fieldValidationErrors = this.state.formErrors;
        let nameValid = this.state.nameValid;
        let startDateValid = this.state.startDateValid;
        let endDateValid = this.state.endDateValid;

        switch (fieldName) {
            case 'name':
                nameValid = isNameValid(value);
                fieldValidationErrors.name = nameValid ? '' : ' is invalid';
                break;
            case 'startDate':
                startDateValid = isValueEntered(value);
                fieldValidationErrors.startDate = startDateValid ? '' : ' is invalid';
                break;
            case 'endDate':
                endDateValid = isValueEntered(value);
                fieldValidationErrors.endDate = endDateValid ? '' : ' is invalid';
                break;
            default:
                break;
        }
        this.setState({
            formErrors: fieldValidationErrors,
            nameValid: nameValid,
            startDateValid: startDateValid,
            endDateValid: endDateValid,
        }, this.validateForm);
    }

    validateForm() {
        const datesAreValid = this.datesValid();
        this.setState({ formValid: this.state.nameValid && datesAreValid });
    }

    datesValid() {
        const { startDate, endDate } = this.state;
        let fieldValidationErrors = this.state.formErrors;
        if (isValueEntered(startDate) && isValueEntered(endDate)) {
            if (isDateBefore(endDate, startDate)) {
                fieldValidationErrors.dates = ' are invalid. The end date selected cannot be before the start date';
                this.setState({ formErrors: fieldValidationErrors });
                return false;
            }
            fieldValidationErrors.dates = '';
            this.setState({ formErrors: fieldValidationErrors });
            return true;
        }
        return true;
    }

    handleStartDateChange(selectedDay) {
        this.setState({ startDate: selectedDay },
            () => { this.validateField('startDate', selectedDay); });
    }

    handleEndDateChange(selectedDay) {
        this.setState({ endDate: selectedDay },
            () => { this.validateField('endDate', selectedDay); });
    }

    render() {
        const { onSaveProjectIssue, showProjectIssueLoader, onProjectIssueClose, open, projectIssue } = this.props;
        const { id,
            orgid,
            name,
            description,
            startDate,
            endDate,
            starred,
            selected,
            frequently
        } = this.state;
        const isNew = projectIssue.name === '';
        let { thumb } = this.state;
        return (
            <Modal className="modal-box" toggle={onProjectIssueClose} isOpen={open}>
                <ModalHeader className="modal-box-header bg-primary">
                    {isNew ? <IntlMessages id="projectIssue.addProjectIssue" /> :
                        <IntlMessages id="projectIssue.saveProjectIssue" />}
                    <span className="text-white pointer">
                        <i className="zmdi zmdi-close zmdi-hc-lg" onClick={onProjectIssueClose} />
                    </span>
                </ModalHeader>

                <div className="modal-box-content">
                    <div className="row no-gutters">
                        <div className="col-lg-3 text-center text-lg-right order-lg-2">
                            <img id="thumb" className="ml-lg-3 mb-4 mb-lg-0 avatar size-120" src={thumb} />
                        </div>

                        <div className="panel panel-default">
                            <FormErrors formErrors={this.state.formErrors} />
                        </div>

                        <div className="col-lg-9 d-flex flex-column order-lg-1">
                            <h3 className="card-heading">{<IntlMessages id="label.name" />}</h3>
                            <input type="text" className={`form-control mb-2 ${name ? 'is-valid' : 'is-invalid'}`}
                                onChange={(event) => this.handleUserInput(event)}
                                defaultValue={name} name="name"
                            />
                            <h3 className="card-heading">{<IntlMessages id="label.description" />}</h3>
                            <input type="text" className="form-control mb-2"
                                onChange={(event) => this.handleUserInput(event)}
                                defaultValue={description} name="description"
                            />
                            <h3 className="card-heading mt-2">{<IntlMessages id="label.startDate" />}</h3>
                            <div className="form-control">
                                <DayPickerInput
                                    value={formatDateDefault(startDate)}
                                    onDayChange={this.handleStartDateChange}
                                    dayPickerProps={{
                                        selectedDays: new Date(startDate),
                                    }}
                                />
                            </div>
                            <h3 className="card-heading mt-2">{<IntlMessages id="label.endDate" />}</h3>
                            <div className="form-control">
                                <DayPickerInput
                                    value={formatDateDefault(endDate)}
                                    onDayChange={this.handleEndDateChange}
                                    dayPickerProps={{
                                        selectedDays: new Date(endDate),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-box-footer d-flex flex-row">
                    <Button className="text-uppercase" disabled={!this.state.formValid} color="primary" onClick={() => {
                        showProjectIssueLoader();
                        onProjectIssueClose();
                        onSaveProjectIssue({
                            'id': id,
                            'orgid': orgid,
                            'name': name,
                            'description': description,
                            'startDate': startDate,
                            'endDate': endDate,
                            'starred': starred,
                            'selected': selected,
                            'frequently': frequently,
                            'thumb': null
                        });
                        this.setState({
                            id: '',
                            orgid: '',
                            name: '',
                            description: '',
                            startDate: '',
                            endDate: '',
                            thumb: null,
                            selected: false,
                            starred: false,
                            frequently: false,
                            formErrors: { name: '', startDate: '', endDate: '', dates: '' },
                            datesValid: false,
                            formValid: false,
                        });

                    }}>
                        {isNew ? <IntlMessages id="projectIssue.addProjectIssue" /> :
                            <IntlMessages id="projectIssue.saveProjectIssue" />}</Button>
                </div>
            </Modal >
        );
    }
}

export default ProjectIssueAdd;


// WEBPACK FOOTER //
// ./components/Agile/projectIssue/projectIssueAdd/index.js